import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiCheck,
  FiArrowRight,
  FiTrendingUp,
  FiClock,
  FiCalendar,
  FiAward,
  FiTarget,
  FiStar,
  FiGlobe,
  FiX,
} from 'react-icons/fi';
import {
  API_BASE_URL,
  clearAllCaches,
  getAccountScopedLocalStorageKey,
  toastdisplay,
  logout,
} from '../../../utils/utils';
import { getStoredUser } from '../../../utils/authSession';
import { getErrorMessage } from '../../../utils/getErrorMessage';
import Sidebar from '../../Sidebar';
import './TAGEStudyPlans.css';
import { tageApi } from './tageApi';

const TAGEStudyPlans = () => {
  const navigate = useNavigate();
  const user = getStoredUser();
  const userEmail = user?.email;

  const [selectedPlan, setSelectedPlan] = useState(null);
  const [schoolSelections, setSchoolSelections] = useState(null);
  const [diagnosticResults, setDiagnosticResults] = useState(null);
  const [selectedSchoolForPlans, setSelectedSchoolForPlans] = useState('top1'); // top1, top2, or top3
  const plansRef = useRef(null);

  const getScopedLocalStorageItem = (key) => {
    const scopedKey = getAccountScopedLocalStorageKey(key, userEmail);
    return scopedKey ? localStorage.getItem(scopedKey) : null;
  };

  // Time selection modal states
  const [showTimeModal, setShowTimeModal] = useState(false);
  const [preferredTime, setPreferredTime] = useState('09:00');
  const [timezone, setTimezone] = useState('');
  const [displayTimezone, setDisplayTimezone] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Study plans state
  const [studyPlans, setStudyPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(true);
  const [toast, setToast] = useState({
    show: false,
    message: '',
    success: true,
  });

  // Load study plans from backend
  useEffect(() => {
    const loadStudyPlans = async () => {
      setPlansLoading(true);
      setError(null);

      try {
        console.log('📡 Fetching study plans from backend...');
        const response = await tageApi.getStudyPlans();

        if (response.success && response.study_plans) {
          // Transform backend format to frontend format
          const transformedPlans = response.study_plans.map((plan) => ({
            id: plan.id, // Integer database ID for enrollment
            planId: plan.plan_id, // String identifier for display
            name: plan.name,
            description: plan.description,
            duration: plan.duration,
            frequency: plan.frequency,
            hoursPerWeek: plan.hours_per_week,
            difficulty: plan.difficulty,
            testCount: plan.test_count,
            targetScoreRange: {
              min: plan.target_score_range.min,
              max: plan.target_score_range.max,
            },
            totalDays: plan.total_days,
            phases: [], // Phases will be generated on backend during enrollment
          }));

          setStudyPlans(transformedPlans);
          console.log(
            '✅ Study plans loaded from backend:',
            transformedPlans.length
          );
        } else {
          throw new Error('Format de réponse invalide');
        }
      } catch (error) {
        console.error('❌ Error loading study plans from backend:', error);

        // Display appropriate toast based on error type
        let errorMessage =
          "Impossible de charger les plans d'étude. Veuillez réessayer.";

        if (error.message === 'AUTH_REQUIRED') {
          errorMessage =
            "Vous devez être connecté pour accéder aux plans d'étude";
        } else if (error.message === 'SESSION_EXPIRED') {
          errorMessage = 'Session expirée. Veuillez vous reconnecter.';
        } else if (error.message === 'NETWORK_ERROR') {
          errorMessage =
            'Erreur de connexion. Vérifiez votre connexion internet.';
        }

        toastdisplay(errorMessage, false, 3000, setToast);
        setTimeout(async () => {
          await logout();
        }, 3000);
        setError(errorMessage);
        setStudyPlans([]);
      } finally {
        setPlansLoading(false);
      }
    };

    loadStudyPlans();
  }, []);

  // Load user data
  useEffect(() => {
    const savedSchools = getScopedLocalStorageItem('tage_school_selections');
    const savedResults = getScopedLocalStorageItem('tage_diagnostic_results');

    if (savedSchools) {
      setSchoolSelections(JSON.parse(savedSchools));
    }
    if (savedResults) {
      setDiagnosticResults(JSON.parse(savedResults));
    }
  }, [userEmail]);

  // Detect user's timezone on component mount
  useEffect(() => {
    try {
      const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      setTimezone(userTimezone);
      setDisplayTimezone(userTimezone);
    } catch (error) {
      console.error('Error detecting timezone:', error);
      setTimezone('UTC');
      setDisplayTimezone('UTC');
    }
  }, []);

  // Get all plans matching a target score
  const getPlansForTargetScore = (targetScore) => {
    // Filter plans where the target score falls within the plan's target score range
    return studyPlans.filter((plan) => {
      return (
        targetScore >= plan.targetScoreRange.min &&
        targetScore <= plan.targetScoreRange.max
      );
    });
  };

  // Get the highest target score among all selected schools
  const getHighestTargetScore = () => {
    if (!schoolSelections) return 0;

    const scores = [
      schoolSelections.top1?.recommendedScore || 0,
      schoolSelections.top2?.recommendedScore || 0,
      schoolSelections.top3?.recommendedScore || 0,
    ];

    return Math.max(...scores);
  };

  // Get the target score for the currently selected school
  const getSelectedSchoolScore = () => {
    if (!schoolSelections) return 0;
    return schoolSelections[selectedSchoolForPlans]?.recommendedScore || 0;
  };

  // Get recommended plan (default: balanced plan with moderate difficulty)
  const getRecommendedPlan = () => {
    if (!schoolSelections?.top1) return null;

    const targetScore = getSelectedSchoolScore();
    const matchingPlans = getPlansForTargetScore(targetScore);

    // Prefer plans with "Modéré" difficulty or longest duration
    const recommendedPlan = matchingPlans.find(
      (plan) => plan.difficulty === 'Modéré'
    );

    return recommendedPlan || matchingPlans[0] || null;
  };

  // Get all plans based on the selected school's target score
  const getPlansForAllSchools = () => {
    if (!schoolSelections) return [];
    const targetScore = getSelectedSchoolScore();
    return getPlansForTargetScore(targetScore);
  };

  // Get schools that are covered by the selected school's score
  const getCoveredSchools = () => {
    if (!schoolSelections) return [];

    const selectedScore = getSelectedSchoolScore();
    const covered = [];

    // Check each school
    if (
      schoolSelections.top1 &&
      selectedSchoolForPlans !== 'top1' &&
      schoolSelections.top1.recommendedScore <= selectedScore
    ) {
      covered.push(schoolSelections.top1.name);
    }
    if (
      schoolSelections.top2 &&
      selectedSchoolForPlans !== 'top2' &&
      schoolSelections.top2.recommendedScore <= selectedScore
    ) {
      covered.push(schoolSelections.top2.name);
    }
    if (
      schoolSelections.top3 &&
      selectedSchoolForPlans !== 'top3' &&
      schoolSelections.top3.recommendedScore <= selectedScore
    ) {
      covered.push(schoolSelections.top3.name);
    }

    return covered;
  };

  // Handle school selection for plans
  const handleSchoolClick = (schoolKey) => {
    setSelectedSchoolForPlans(schoolKey);
    setSelectedPlan(null); // Reset selected plan when changing school

    // Scroll to plans section
    setTimeout(() => {
      if (plansRef.current) {
        plansRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }
    }, 100);
  };

  // Handle plan selection
  const handleSelectPlan = (plan) => {
    setSelectedPlan(plan);
  };

  // Handle continue - show time selection modal
  const handleContinue = () => {
    if (selectedPlan) {
      setShowTimeModal(true);
    }
  };

  // Handle enrollment with time preference
  const handleEnroll = async () => {
    if (!userEmail) {
      setError('Vous devez être connecté pour vous enrôler');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Prepare all selected schools
      const targetSchools = [];
      if (schoolSelections?.top1) targetSchools.push(schoolSelections.top1);
      if (schoolSelections?.top2) targetSchools.push(schoolSelections.top2);
      if (schoolSelections?.top3) targetSchools.push(schoolSelections.top3);

      // Prepare enrollment data matching backend structure
      const enrollmentData = {
        study_plan_id: selectedPlan.id, // Send plan_id from backend
        duration_days: selectedPlan.totalDays, // Total days from plan
        lessons_per_day: 1, // Default to 1 lesson per day
        preferred_time: preferredTime,
        timezone: timezone,
        plan_name: selectedPlan.name,
        plan_duration_months: parseInt(selectedPlan.duration.split(' ')[0]),
        hours_per_week: selectedPlan.hoursPerWeek,
        target_schools: targetSchools,
        study_plan_phases:
          selectedPlan.phases && selectedPlan.phases.length > 0
            ? selectedPlan.phases.reduce((acc, phase, index) => {
                acc[`phase${index + 1}`] = phase.name;
                return acc;
              }, {})
            : {
                phase1: 'Fondations',
                phase2: 'Perfectionnement',
                phase3: 'Simulations',
              },
      };

      // Call backend API using tageApi
      console.log('Enrolling user with data:', enrollmentData);
      const result = await tageApi.enrollUser(enrollmentData);

      if (result.success) {
        console.log('✅ Enrollment successful:', result.message);
        console.log(
          '📚 Initial lessons received:',
          result.initial_lessons?.length || 0
        );

        // Clear enrollment cache to force refresh from backend
        const enrollmentCacheKey = `tage_enrollment_${userEmail}`;
        localStorage.removeItem(enrollmentCacheKey);
        localStorage.removeItem(`${enrollmentCacheKey}_timestamp`);

        // Store initial lessons directly from enrollment response
        if (result.initial_lessons && result.initial_lessons.length > 0) {
          console.log('✅ Storing initial lessons for immediate display');
          // Store in a temporary key that TAGE.js will check first
          sessionStorage.setItem(
            'tage_initial_lessons',
            JSON.stringify(result.initial_lessons)
          );
        }

        // Close modal and navigate to TAGE dashboard
        setShowTimeModal(false);
        navigate('/exams/tage');
      } else {
        throw new Error(result.message || "Échec de l'enrôlement");
      }
    } catch (err) {
      console.error('❌ Enrollment error:', err);

      // Display appropriate toast based on error type
      let errorMessage =
        "Une erreur s'est produite lors de l'enrôlement. Veuillez réessayer.";

      if (err.message === 'AUTH_REQUIRED') {
        errorMessage = 'Vous devez être connecté pour vous inscrire.';
      } else if (err.message === 'SESSION_EXPIRED') {
        errorMessage = 'Session expirée. Veuillez vous reconnecter.';
      } else if (err.message === 'VALIDATION_ERROR') {
        errorMessage = 'Données invalides. Veuillez vérifier vos informations.';
      } else if (err.message === 'NETWORK_ERROR') {
        errorMessage =
          'Erreur de connexion. Vérifiez votre connexion internet.';
      }

      toastdisplay(errorMessage, false, 3000, setToast);
      setTimeout(() => {
        logout();
      }, 3000);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Get difficulty badge color
  const getDifficultyColor = (difficulty) => {
    switch (difficulty.toLowerCase()) {
      case 'élevé':
      case 'très élevé':
      case 'extrême':
        return 'studyplans-difficulty-high';
      case 'modéré':
      case 'modéré à élevé':
        return 'studyplans-difficulty-medium';
      case 'accessible':
      case 'accessible à modéré':
        return 'studyplans-difficulty-low';
      default:
        return 'studyplans-difficulty-medium';
    }
  };

  const recommendedPlan = getRecommendedPlan();
  const availablePlans = getPlansForAllSchools();

  return (
    <div className="layout">
      <Sidebar />
      {toast.show && (
        <div
          className={`toast-notification ${toast.success ? 'success' : 'error'}`}
        >
          <span className="toast-icon">{toast.success ? '✔️' : '❌'}</span>
          <span>{toast.message}</span>
        </div>
      )}
      <div className="content">
        <div className="studyplans-container">
          {/* Header */}
          <div className="studyplans-header">
            <div className="studyplans-header-icon">
              <FiTrendingUp size={40} />
            </div>
            <h1>Vos Plans d'Étude Personnalisés</h1>
            <p className="studyplans-header-subtitle">
              Basés sur votre test diagnostic et vos objectifs, nous vous
              proposons 3 programmes adaptés. Choisissez celui qui correspond le
              mieux à votre emploi du temps et vos ambitions.
            </p>
          </div>

          {/* User Context */}
          {schoolSelections && diagnosticResults && (
            <div className="studyplans-user-context">
              <div className="studyplans-context-card">
                <h3>
                  <FiTarget size={20} /> Vos Objectifs
                </h3>
                <p
                  style={{
                    fontSize: '14px',
                    color: '#6b7280',
                    marginBottom: '15px',
                  }}
                >
                  Cliquez sur une école pour voir les plans adaptés
                </p>

                {/* Top 1 School */}
                <div
                  onClick={() => handleSchoolClick('top1')}
                  style={{
                    padding: '15px',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    border:
                      selectedSchoolForPlans === 'top1'
                        ? '2px solid #6366f1'
                        : '2px solid transparent',
                    backgroundColor:
                      selectedSchoolForPlans === 'top1'
                        ? '#f0f1ff'
                        : 'transparent',
                    transition: 'all 0.2s ease',
                  }}
                >
                  <p className="studyplans-school-target">
                    <strong>{schoolSelections.top1.name}</strong> -{' '}
                    {schoolSelections.top1.program}
                  </p>
                  <div className="studyplans-target-scores">
                    <div className="studyplans-target-score-item">
                      <span>Score conseillé</span>
                      <strong>{schoolSelections.top1.recommendedScore}</strong>
                    </div>
                  </div>
                  {selectedSchoolForPlans === 'top1' && (
                    <span
                      style={{
                        fontSize: '12px',
                        color: '#6366f1',
                        fontWeight: 'bold',
                        marginTop: '5px',
                        display: 'block',
                      }}
                    >
                      ✓ Plans affichés pour cette école
                    </span>
                  )}
                </div>

                {/* Top 2 School */}
                {schoolSelections.top2 && (
                  <>
                    <p
                      style={{
                        marginTop: '10px',
                        marginBottom: '10px',
                        textAlign: 'center',
                        color: '#9ca3af',
                        fontWeight: 'bold',
                      }}
                    >
                      Ou
                    </p>
                    <div
                      onClick={() => handleSchoolClick('top2')}
                      style={{
                        padding: '15px',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        border:
                          selectedSchoolForPlans === 'top2'
                            ? '2px solid #6366f1'
                            : '2px solid transparent',
                        backgroundColor:
                          selectedSchoolForPlans === 'top2'
                            ? '#f0f1ff'
                            : 'transparent',
                        transition: 'all 0.2s ease',
                      }}
                    >
                      <p className="studyplans-school-target">
                        <strong>{schoolSelections.top2.name}</strong> -{' '}
                        {schoolSelections.top2.program}
                      </p>
                      <div className="studyplans-target-scores">
                        <div className="studyplans-target-score-item">
                          <span>Score conseillé</span>
                          <strong>
                            {schoolSelections.top2.recommendedScore}
                          </strong>
                        </div>
                      </div>
                      {selectedSchoolForPlans === 'top2' && (
                        <span
                          style={{
                            fontSize: '12px',
                            color: '#6366f1',
                            fontWeight: 'bold',
                            marginTop: '5px',
                            display: 'block',
                          }}
                        >
                          ✓ Plans affichés pour cette école
                        </span>
                      )}
                    </div>
                  </>
                )}

                {/* Top 3 School */}
                {schoolSelections.top3 && (
                  <>
                    <p
                      style={{
                        marginTop: '10px',
                        marginBottom: '10px',
                        textAlign: 'center',
                        color: '#9ca3af',
                        fontWeight: 'bold',
                      }}
                    >
                      Ou
                    </p>
                    <div
                      onClick={() => handleSchoolClick('top3')}
                      style={{
                        padding: '15px',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        border:
                          selectedSchoolForPlans === 'top3'
                            ? '2px solid #6366f1'
                            : '2px solid transparent',
                        backgroundColor:
                          selectedSchoolForPlans === 'top3'
                            ? '#f0f1ff'
                            : 'transparent',
                        transition: 'all 0.2s ease',
                      }}
                    >
                      <p className="studyplans-school-target">
                        <strong>{schoolSelections.top3.name}</strong> -{' '}
                        {schoolSelections.top3.program}
                      </p>
                      <div className="studyplans-target-scores">
                        <div className="studyplans-target-score-item">
                          <span>Score conseillé</span>
                          <strong>
                            {schoolSelections.top3.recommendedScore}
                          </strong>
                        </div>
                      </div>
                      {selectedSchoolForPlans === 'top3' && (
                        <span
                          style={{
                            fontSize: '12px',
                            color: '#6366f1',
                            fontWeight: 'bold',
                            marginTop: '5px',
                            display: 'block',
                          }}
                        >
                          ✓ Plans affichés pour cette école
                        </span>
                      )}
                    </div>
                  </>
                )}
              </div>

              <div className="studyplans-context-card">
                <h3>
                  <FiAward size={20} /> Votre Niveau Actuel
                </h3>
                <p className="studyplans-current-score">
                  Score estimé :{' '}
                  <strong>{diagnosticResults.estimatedScore}</strong>
                </p>
                <p className="studyplans-score-gap">
                  Points à gagner pour l'école sélectionnée :{' '}
                  <strong>
                    {Math.max(
                      0,
                      getSelectedSchoolScore() -
                        diagnosticResults.estimatedScore
                    )}
                  </strong>
                </p>
              </div>
            </div>
          )}

          {/* Study Plans */}
          {schoolSelections && (
            <div
              ref={plansRef}
              style={{
                marginBottom: '20px',
                textAlign: 'center',
                scrollMarginTop: '100px',
              }}
            >
              <h2
                style={{
                  fontSize: '20px',
                  fontWeight: 'bold',
                  color: '#1f2937',
                }}
              >
                Plans pour {schoolSelections[selectedSchoolForPlans]?.name}
                {getCoveredSchools().length > 0 && (
                  <span style={{ fontWeight: 'normal' }}>
                    {' '}
                    ainsi que {getCoveredSchools().join(' et ')}
                  </span>
                )}
              </h2>
              <p style={{ fontSize: '14px', color: '#6b7280' }}>
                Score cible :{' '}
                {schoolSelections[selectedSchoolForPlans]?.recommendedScore}
              </p>
              {getCoveredSchools().length > 0 && (
                <p
                  style={{
                    fontSize: '13px',
                    color: '#10b981',
                    fontWeight: '500',
                    marginTop: '8px',
                  }}
                >
                  ✓ Avec ce score, vous pouvez également atteindre{' '}
                  {getCoveredSchools().length === 1
                    ? 'cette école'
                    : 'ces écoles'}
                </p>
              )}
            </div>
          )}

          {/* Error Display */}
          {error && (
            <div
              style={{
                gridColumn: '1 / -1',
                backgroundColor: '#fee2e2',
                border: '1px solid #fca5a5',
                borderRadius: '8px',
                padding: '20px',
                marginBottom: '20px',
                textAlign: 'center',
              }}
            >
              <div
                style={{
                  color: '#dc2626',
                  fontWeight: 'bold',
                  marginBottom: '10px',
                }}
              >
                ❌ Erreur de chargement
              </div>
              <div style={{ color: '#991b1b', marginBottom: '15px' }}>
                {getErrorMessage(error, 'Impossible de charger les plans.')}
              </div>
              <button
                onClick={() => window.location.reload()}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#dc2626',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: 'bold',
                }}
              >
                Réessayer
              </button>
            </div>
          )}

          <div className="studyplans-grid">
            {plansLoading ? (
              <div
                style={{
                  gridColumn: '1 / -1',
                  textAlign: 'center',
                  padding: '40px',
                  color: '#6b7280',
                }}
              >
                <div style={{ marginBottom: '10px' }}>
                  ⏳ Chargement des plans d'étude...
                </div>
              </div>
            ) : error ? (
              <div
                style={{
                  gridColumn: '1 / -1',
                  textAlign: 'center',
                  padding: '40px',
                  color: '#dc2626',
                }}
              >
                <div>Impossible de charger les plans d'étude</div>
              </div>
            ) : availablePlans.length === 0 ? (
              <div
                style={{
                  gridColumn: '1 / -1',
                  textAlign: 'center',
                  padding: '40px',
                  color: '#6b7280',
                }}
              >
                <div>Aucun plan d'étude disponible pour votre profil</div>
              </div>
            ) : (
              availablePlans.map((plan) => (
                <div
                  key={plan.id}
                  className={`studyplans-plan-card ${selectedPlan?.id === plan.id ? 'selected' : ''} ${recommendedPlan?.id === plan.id ? 'recommended' : ''}`}
                  onClick={() => handleSelectPlan(plan)}
                >
                  {recommendedPlan?.id === plan.id && (
                    <div className="studyplans-recommended-badge">
                      <FiStar size={16} /> Recommandé pour vous
                    </div>
                  )}
                  <div className="studyplans-plan-header">
                    <h3>{plan.name}</h3>
                    <p className="studyplans-plan-subtitle">
                      Pour atteindre{' '}
                      {schoolSelections?.[selectedSchoolForPlans]?.name}
                      {getCoveredSchools().length > 0 && (
                        <span>
                          {' '}
                          ainsi que {getCoveredSchools().join(' et ')}
                        </span>
                      )}
                    </p>
                  </div>

                  <div className="studyplans-plan-description">
                    <p>{plan.description}</p>
                  </div>

                  <div className="studyplans-plan-details">
                    <div className="studyplans-detail-item">
                      <FiClock size={18} />
                      <div>
                        <span className="studyplans-detail-label">Durée</span>
                        <span className="studyplans-detail-value">
                          {plan.duration}
                        </span>
                      </div>
                    </div>
                    <div className="studyplans-detail-item">
                      <FiCalendar size={18} />
                      <div>
                        <span className="studyplans-detail-label">
                          Fréquence
                        </span>
                        <span className="studyplans-detail-value">
                          {plan.frequency}
                        </span>
                      </div>
                    </div>
                    <div className="studyplans-detail-item">
                      <FiTrendingUp size={18} />
                      <div>
                        <span className="studyplans-detail-label">
                          Heures/semaine
                        </span>
                        <span className="studyplans-detail-value">
                          {plan.hoursPerWeek}h
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="studyplans-plan-target-score">
                    <span className="studyplans-target-label">Score visé</span>
                    <span className="studyplans-target-range">
                      {plan.targetScoreRange.min} - {plan.targetScoreRange.max}
                    </span>
                  </div>

                  <div className="studyplans-plan-difficulty">
                    <span
                      className={`studyplans-difficulty-badge ${getDifficultyColor(plan.difficulty)}`}
                    >
                      {plan.difficulty}
                    </span>
                  </div>

                  {plan.phases && plan.phases.length > 0 && (
                    <div className="studyplans-plan-phases">
                      <h4>Phases du programme</h4>
                      <ul>
                        {plan.phases.map((phase, index) => (
                          <li key={index}>
                            <strong>Phase {phase.phase}:</strong> {phase.name} (
                            {phase.duration})
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {selectedPlan?.id === plan.id && (
                    <div className="studyplans-selected-indicator">
                      <FiCheck size={20} /> Sélectionné
                    </div>
                  )}
                </div>
              ))
            )}
          </div>

          {/* Continue Button */}
          {selectedPlan ? (
            <div className="studyplans-actions">
              <button
                className="btn-lwz-primary studyplans-btn-continue-large"
                onClick={handleContinue}
              >
                Commencer mon programme
                <FiArrowRight size={20} />
              </button>
            </div>
          ) : (
            <div className="studyplans-hint">
              <p>Sélectionnez un plan d'étude pour continuer</p>
            </div>
          )}
        </div>

        {/* Time Selection Modal */}
        {showTimeModal && selectedPlan && (
          <div
            className="tage-time-modal-overlay"
            onClick={() => setShowTimeModal(false)}
          >
            <div
              className="tage-time-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="tage-time-modal-header">
                <div>
                  <h2>Choisissez votre horaire de cours</h2>
                  <p>
                    À quelle heure souhaitez-vous recevoir vos leçons
                    quotidiennes?
                  </p>
                </div>
                <button
                  className="tage-time-modal-close"
                  onClick={() => setShowTimeModal(false)}
                >
                  <FiX size={24} />
                </button>
              </div>

              <div className="tage-time-modal-body">
                {/* Selected Plan Summary */}
                <div className="tage-time-plan-summary">
                  <FiTarget size={20} />
                  <div>
                    <h3>{selectedPlan.name}</h3>
                    <p>
                      {selectedPlan.duration} • {selectedPlan.frequency}
                    </p>
                  </div>
                </div>

                {/* Timezone Info */}
                <div className="tage-time-timezone-info">
                  <FiGlobe size={18} />
                  <span>Votre fuseau horaire: {displayTimezone}</span>
                </div>

                {/* Time Selection */}
                <div className="tage-time-selection-label">
                  <FiClock size={18} />
                  <span>Sélectionnez votre heure préférée</span>
                </div>

                <div className="tage-time-grid">
                  {[
                    { time: '06:00', label: '6:00', desc: 'Tôt le matin' },
                    { time: '07:00', label: '7:00', desc: 'Matin' },
                    { time: '08:00', label: '8:00', desc: 'Matin' },
                    { time: '09:00', label: '9:00', desc: 'Milieu de matinée' },
                    { time: '10:00', label: '10:00', desc: 'Fin de matinée' },
                    { time: '12:00', label: '12:00', desc: 'Midi' },
                    {
                      time: '14:00',
                      label: '14:00',
                      desc: "Début d'après-midi",
                    },
                    { time: '16:00', label: '16:00', desc: 'Après-midi' },
                    { time: '18:00', label: '18:00', desc: 'Soirée' },
                    { time: '20:00', label: '20:00', desc: 'Soirée' },
                    { time: '22:00', label: '22:00', desc: 'Tard le soir' },
                  ].map((timeOption) => (
                    <button
                      key={timeOption.time}
                      onClick={() => setPreferredTime(timeOption.time)}
                      className={`tage-time-option ${preferredTime === timeOption.time ? 'selected' : ''}`}
                    >
                      <div className="tage-time-label">{timeOption.label}</div>
                      <div className="tage-time-desc">{timeOption.desc}</div>
                    </button>
                  ))}
                </div>

                {error && (
                  <div className="tage-time-error">
                    {getErrorMessage(error, 'Une erreur est survenue.')}
                  </div>
                )}

                {/* Enrollment Summary */}
                <div className="tage-time-summary">
                  <p className="tage-time-summary-title">Votre Programme</p>
                  <p className="tage-time-summary-text">
                    Vous recevrez vos leçons quotidiennes à{' '}
                    <strong>{preferredTime}</strong> ({displayTimezone}). Le
                    programme commence dès maintenant et s'adaptera
                    automatiquement à votre emploi du temps.
                  </p>
                </div>

                {/* Action Buttons */}
                <div className="tage-time-modal-actions">
                  <button
                    className="btn-lwz-secondary"
                    onClick={() => setShowTimeModal(false)}
                    disabled={loading}
                  >
                    Annuler
                  </button>
                  <button
                    className="btn-lwz-primary"
                    onClick={handleEnroll}
                    disabled={loading}
                  >
                    {loading
                      ? 'Enrôlement en cours...'
                      : 'Commencer mon programme'}
                    {!loading && <FiArrowRight size={18} />}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TAGEStudyPlans;
